
import { defineComponent, PropType, ref } from "@vue/runtime-core";
import { Estimation } from "@/types/estimation.type";
import { Image } from "@/types/Image.type";
import { Parameter } from "@/types/parameter.type";
import { getImage } from "@/service/image.service";
import { getTmoaEstimation, findEstimation, checkNameTmoa } from "@/utils/estimation.util";
import securityService from "@/service/security.service";
import { AppRole } from "@/types/app.type";

interface ImageData extends Image {
  title?: string;
}
interface EstimationWithTmoa extends Estimation {
  tmoaName?: string | number;
}

export default defineComponent({
  props: {
    thumbnail: {
      type: Boolean,
      default: true,
      required: false,
    },
    images: {
      type: Array as PropType<ImageData[]>,
      required: true,
    },
    edit: {
      type: Boolean,
      default: false,
      required: false,
    },
    options: {
      type: Object as () => Array<{ label: string; value: string }>,
      required: false,
    },
    functionBtn: {
      type: Function,
      required: false,
    },
    paramsVisible: {
      type: Boolean,
      default: true,
      required: false,
    },
  },
  setup() {
    const isAppDemo = securityService.hasRole(AppRole.DEMOS) ? true : false;
    return { findEstimation, checkNameTmoa, isAppDemo };
  },
  data() {
    return {
      src: ref<{ [name: string]: string }>({}),
      allSamplingPointParams: [] as Parameter[],
      activeBtnSaveMap: [] as boolean[],
      loading: true,
      tmoaCache: {} as Record<string | number, string>,
      TARGET_POINTS: ["EDAR_VALL_BIO", "EDAR_VALL_CAN", "EDAR_VALL_CAN_2", "EDAR_VALL_CAS"],
    };
  },
  async created() {
    this.$emit("loading");
    await this.loadImage();
    this.loading = false;
    this.$emit("loaded");
  },
  unmounted() {
    // - free memory
    Object.values(this.src).forEach((value) => URL.revokeObjectURL(value));
  },
  methods: {
    async loadImage() {
      this.images.forEach(async (image) => {
        if (image?.fileName && !this.src[image.fileName]) {
          getImage(image.fileName, this.thumbnail)
            .then((res) => {
              this.activeBtnSaveMap.push(true);
              if (image && image.fileName) {
                this.src[image.fileName] = res;
              }
            })
            .catch(void 0);
        }

        if (image.imageEstimation) {
          const index = image.imageEstimation.findIndex(
            (estimation) => "parameter" in estimation && estimation.parameter?.code === "TMOA"
          );

          if (index !== -1 && this.images[0].samplingPoint) {
            const est = image.imageEstimation[index] as EstimationWithTmoa;
            const value = est.value;

            if (value !== undefined && this.tmoaCache[value] === undefined) {
              const tmoa = await getTmoaEstimation(this.images[0].samplingPoint, value);
              this.tmoaCache[value] = tmoa?.name || "-";
            }

            if (value !== undefined) {
              est.tmoaName = this.tmoaCache[value] || "-";
            } else {
              est.tmoaName = "-";
            }
          }
        }
      });
    },
    reorderArray(arr: Parameter[]) {
      return arr.sort((a, b) => (a.code === "ALT" ? -1 : b.code === "ALT" ? 1 : 0));
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    formatDate(image: any) {
      let dateTime2 = this.$d(new Date(`${image.dateTime}`), "dateTime");
      return dateTime2 !== undefined ? dateTime2 : "-";
    },
    getNameParameter(parameter: Parameter | undefined) {
      let name = "";
      if (parameter && parameter.name) {
        if (parameter.name.length > 15) {
          name = parameter.abbreviation || parameter.name;
        } else {
          name = parameter.name;
        }
      }
      return name;
    },
    getUnitParameter(parameter: Parameter | undefined) {
      return parameter?.unit || "";
    },
    filterEstimations(estimations: Estimation[]): Estimation[] {
      let hasAlt = false;
      let hasTmoa = false;

      const filtered = estimations.filter((estimation) => {
        const code = estimation.parameter?.code;

        if (code === "ALT") {
          if (hasAlt) return false;
          hasAlt = true;
          return true;
        }

        if (code === "TMOA") {
          if (hasTmoa) return false;
          hasTmoa = true;
          return true;
        }
        return true;
      });

      const order = [
        "Estado",
        "Turbidez",
        "Sólidos en Suspensión",
        "Sólidos disueltos",
        "MES",
        "Conductividad",
        "REDOX",
        "DQO",
        "DQ",
        "DBO5 | DBO",
        "DBO",
        "DB",
        "Demanda química de oxígeno",
        "Carbono Orgánico Disuelto",
        "Carbono orgánico disuelto (COD)",
        "COD",
        "COT",
        "Carbono Orgánico Total",
        "Nitrógeno Total",
        "Nitrógeno",
        "Nitrato",
        "Oxígeno Disuelto",
        "pH",
        "Fósforo",
        "Fosfato",
        "Ortofosfato",
        "Amonio",
        "Hierro",
        "Manganeso",
        "Aceites y grasas",
        "Espumas",
        "Lentejas",
        "Retorno",
        "Color",
        "Algas verdeazuladas",
        "Algas verdes",
        "Clorofila A (algas verdeazuladas)",
        "Clorofila A (algas verdes)",
        "Clorofila A (diatomeas)",
        "Clorofila A (cryptophytas)",
        "Clorofila A total",
        "Cryptophytas (control de algas superficie)",
        "Diatomeas (control de algas superficie)",
        "Total algas (control de algas superficie)",
        "Nivel",
        "E.coli",
      ];

      return filtered.sort((a, b) => {
        const nameA = a.parameter?.name?.toLowerCase() || "";
        const nameB = b.parameter?.name?.toLowerCase() || "";

        const indexA = order.findIndex((item) => item.toLowerCase() === nameA);
        const indexB = order.findIndex((item) => item.toLowerCase() === nameB);

        const posA = indexA === -1 ? Number.MAX_SAFE_INTEGER : indexA;
        const posB = indexB === -1 ? Number.MAX_SAFE_INTEGER : indexB;

        return posA - posB;
      });
    },
    async updateImage(image: Image, index: number) {
      if (this.functionBtn && image.id) {
        await this.functionBtn(image);
        this.activeBtnSaveMap[index] = true;
      }
    },
  },
  watch: {
    images: {
      handler() {
        this.loading = true;
        this.$emit("loading");
        this.loadImage();
        this.loading = false;
        this.$emit("loaded");
      },
      deep: false,
    },
  },
});
